import React, { FC, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { toastService } from 'components/molecules/toast';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import {
  RateCardsProjectSettngsQueryKey,
  useProjectsBudgetAndRateSettingsMutation,
  useProjectsBudgetAndRateSettingsQuery,
} from 'queries/project';
import { EditBillRateDrawer } from './edit-bill-rate';
import { SimpleEditDrawer } from './simple-edit-drawer';

export enum EditBudgetAndRateTypes {
  BillRate = 'BILL_RATE',
  ProjectBudget = 'PROJECT_BUDGET',
  ProjectHours = 'PROJECT_HOURS',
  FixedFee = 'FIXED_FEE',
}

export const EditBudgetAndRateInfo: FC<{
  visible: boolean;
  setVisiblity: (flag: boolean) => void;
  projectId: number;
  type: EditBudgetAndRateTypes | undefined;
}> = ({ type, visible, projectId, setVisiblity }) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { data } = useProjectsBudgetAndRateSettingsQuery(projectId);
  const { mutate, isLoading } = useProjectsBudgetAndRateSettingsMutation(
    projectId,
    {
      onSuccess: () => {
        queryClient.refetchQueries(RateCardsProjectSettngsQueryKey(projectId));

        toastService({
          title: translate('project.settings.budget.rate.edit.success'),
          message: translate(
            `project.settings.budget.rate.edit.success.message.${type}` as TranslationKey,
          ),
        }).success();
        setVisiblity(false);
      },
    },
  );

  useEffect(() => {
    if (!type) {
      setVisiblity(false);
    }
  }, [setVisiblity, type]);

  const onDrawerClose = () => setVisiblity(false);

  switch (type) {
    case EditBudgetAndRateTypes.BillRate:
      return (
        <EditBillRateDrawer
          visible={visible}
          pageInfo={data}
          saveInProgress={isLoading}
          projectId={projectId}
          onClose={onDrawerClose}
          onSave={mutate}
        />
      );
    case EditBudgetAndRateTypes.ProjectBudget:
    case EditBudgetAndRateTypes.ProjectHours:
    case EditBudgetAndRateTypes.FixedFee:
      return (
        <SimpleEditDrawer
          data={data}
          type={type}
          visible={visible}
          saveInProgress={isLoading}
          onClose={onDrawerClose}
          onSave={mutate}
        />
      );
    default:
      return <></>;
  }
};
